/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useContext, useEffect, useCallback, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import withPageTracking from 'shared/analytics/components/withPageTracking';
import {
    Column,
    Container,
    Divider,
    ErrorBoundary,
    Footer,
    Header,
    NotFoundError,
    Row,
    Sidebar,
    Spacer,
    UserContext,
    ModalContext
} from 'school-space-client/components';
import DocumentProgressBar from 'dashboard/components/cards/DocumentProgressBar';

import { openRebookingInformationModal } from './components/sections/rebooking/modals';
import BookingsScreen from './screens/Bookings';
import PastBookingsScreen from './screens/PastBookings';
import RecurringBookingsScreen from './screens/RecurringBookings';
import RecurringBookingScreen from './screens/RecurringBooking';
import PaymentFailed from './screens/PaymentFailed';
import PaymentSucess from './screens/PaymentSuccess';
import SupportingInformationTabs from './screens/SupportingInformationTabs';
import BacsPaymentSetup from './screens/BacsSetup';
import constants from '../constanst';
import { getPaymentMethods } from 'school-space-client/api';

const navLinks = [
    {
        title: 'Book a space',
        url: 'https://school-space.org/spaces',
        isExternal: true
    },
    {
        title: 'Contact us',
        url: 'https://school-space.org/about-us/contact-us',
        isExternal: true
    },
    {
        title: 'Knowledge base',
        url: 'https://school-spacehelp.zendesk.com/hc/en-gb',
        isExternal: true
    }
];

const sidebarLinks = [
    {
        title: 'Upcoming bookings',
        url: '/bookings',
        icon: '',
        showIcon: false
    },
    {
        title: 'Past bookings',
        url: '/past-bookings',
        icon: '',
        showIcon: false
    },
    {
        title: 'Repeat bookings',
        url: '/repeat-bookings',
        icon: '',
        showIcon: false
    },
    {
        title: 'Supporting Information',
        url: '/supporting-information',
        icon: 'alertOutlined',
        showIcon: true
    },
    {
        title: 'Logout',
        url: '/logout',
        icon: '',
        showIcon: false
    }
];

const footerLinks = [
    {
        title: 'Terms and conditions',
        url: 'https://school-space.org/terms-conditions',
        isExternal: true
    },
    {
        title: 'Cancellation policy',
        url: 'https://school-space.org/terms-conditions/school-space-cancellation-policy',
        isExternal: true
    },
    {
        title: 'Hire charges and deposit terms',
        url: 'https://school-space.org/terms-conditions/hire-charges-deposit-terms',
        isExternal: true
    },
    {
        title: 'Privacy policy',
        url: 'https://school-space.org/privacy-policy',
        isExternal: true
    }
];

const css = {
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh'
    },
    underline: {
        textDecoration: 'underline',
        fontWeight: 700,
        cursor: 'pointer'
    },
    noMargin: {
        margin: 0
    },
    sidebar: theme => ({
        display: 'none',
        [theme.helpers.mq('m')]: {
            display: 'block'
        }
    }),
    contentWrapper: {
        flexGrow: '1'
    },
    content: theme => ({
        [theme.helpers.mq('l')]: { paddingLeft: theme.grid.gutter.l }
    }),
    coronaBanner: {
        background: 'rgba(238, 0, 0, 0.1)',
        padding: '24px',
        borderRadius: '10px',
        margin: ' 0px 0px 20px 0px'
    }
};

const { REACT_APP_BASE_URL } = process.env;

const Dashboard = () => {
    const user = useContext(UserContext);
    const {
        openModal,
        types: { mobileMenu, rebookingInformation }
    } = useContext(ModalContext);

    const [hasPaymentMethod, setHasPaymentMethod] = useState(false);

    const fetchPaymentMethodStatus = useCallback(async () => {
        try {
            if (!user?.id) return;

            const response = await getPaymentMethods(user.id);
            if (Array.isArray(response?.data)) {
                setHasPaymentMethod(response.data.length > 0);
            } else {
                console.warn('Unexpected response from getPaymentMethods:', response);
                setHasPaymentMethod(false);
            }
        } catch (err) {
            console.error('Failed to fetch payment method status:', err);
            setHasPaymentMethod(false);
        }
    }, [user?.id]);

    useEffect(() => {
        fetchPaymentMethodStatus();
    }, []);

    if (user.supportingDocuments.length > 0 && user.customerDeposits !== null) {
        const safeGaurdingPolicy = user.supportingDocuments.filter(
            item => item.documentType === 'safeGaurdingPolicy'
        );
        const riskAssessment = user.supportingDocuments.filter(
            item => item.documentType === 'riskAssessment'
        );
        const liabilityInsurance = user.supportingDocuments.filter(
            item => item.documentType === 'liabilityInsurance'
        );

        if (
            safeGaurdingPolicy.length > 0 &&
            riskAssessment.length > 0 &&
            liabilityInsurance.length > 0
        ) {
            sidebarLinks.map(item => {
                const tempItem = item;
                if (tempItem.title === 'Supporting Information') {
                    tempItem.showIcon = false;
                }
                return tempItem;
            });
        }
    }
    const rebookingInfoSeen = localStorage.getItem('rebookingInfoSeen') === 'true';

    useEffect(() => {
        if (user.showRebooking && user.termsAgreed && !rebookingInfoSeen) {
            openRebookingInformationModal({
                types: { rebookingInformation },
                openModal,
                isClosable: false
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); //openModal, rebookingInformation, user

    if (!user.termsAgreed) {
        return <Redirect to="/welcome" />;
    }
    return (
        <div css={css.wrapper}>
            <Header
                links={navLinks}
                openMenu={() =>
                    openModal({
                        type: mobileMenu,
                        props: {
                            links: [...navLinks, ...sidebarLinks]
                        }
                    })
                }
            />
            <Divider />
            <Container styles={css.contentWrapper}>
                <Spacer height={2.5} />
                <Row verticalAlign="flex-start">
                    <Column styles={css.sidebar} l={[3]} m={[3]}>
                        <Sidebar user={user} links={sidebarLinks} />
                    </Column>
                    <Column styles={css.content} s={[6]} m={[9]} l={[9]}>
                        <ErrorBoundary>
                            {(user.customerDeposits === null || !hasPaymentMethod) && (
                                <div style={css.coronaBanner}>
                                    {user.customerDeposits === null && (
                                        <p style={css.noMargin}>
                                            Your bookings may not be confirmed until a deposit is
                                            paid. Click{' '}
                                            <a
                                                href={`${REACT_APP_BASE_URL}/supporting-information#payment`}
                                                rel="noopener noreferrer"
                                                style={css.underline}
                                            >
                                                here
                                            </a>{' '}
                                            to pay your deposit.
                                        </p>
                                    )}
                                    {user.customerDeposits && !hasPaymentMethod && (
                                        <p style={css.noMargin}>
                                            Please add your payment details to guarantee your
                                            bookings can go ahead. Click{' '}
                                            <a
                                                href={`${REACT_APP_BASE_URL}/supporting-information#payment`}
                                                rel="noopener noreferrer"
                                                style={css.underline}
                                            >
                                                here
                                            </a>{' '}
                                            to complete this step.
                                        </p>
                                    )}
                                </div>
                            )}
                            <DocumentProgressBar
                                supportingDocuments={user.supportingDocuments}
                                documentTypes={constants.documentTypeConst}
                                customerDeposits={user.customerDeposits}
                            />
                            <Switch>
                                <Route exact path="/bookings" component={BookingsScreen} />
                                <Route exact path="/past-bookings" component={PastBookingsScreen} />
                                <Route
                                    exact
                                    path="/repeat-bookings"
                                    component={RecurringBookingsScreen}
                                />
                                <Route
                                    exact
                                    path="/repeat-bookings/:id"
                                    component={RecurringBookingScreen}
                                />
                                <Route
                                    exact
                                    path="/supporting-information"
                                    component={SupportingInformationTabs}
                                />
                                <Route
                                    exact
                                    path="/bacs-payment-setup"
                                    component={BacsPaymentSetup}
                                />
                                <Route exact path="/payment-success" component={PaymentSucess} />
                                <Route exact path="/payment-failed" component={PaymentFailed} />
                                {user.showRebooking && user.termsAgreed && !rebookingInfoSeen ? (
                                    <Route
                                        exact
                                        path="/"
                                        render={() => <Redirect to="/repeat-bookings" />}
                                    />
                                ) : (
                                    <Route
                                        exact
                                        path="/"
                                        render={() => <Redirect to="/bookings" />}
                                    />
                                )}
                                <Route
                                    render={props => (
                                        <NotFoundError {...props} homeDestination="/" />
                                    )}
                                />
                            </Switch>
                        </ErrorBoundary>
                    </Column>
                </Row>
            </Container>
            <Spacer height={3} />
            <Footer links={footerLinks} />
        </div>
    );
};

export default withPageTracking(Dashboard);
